import { Response, Survey } from "@/types";
import { CategoryScores, lowestCategory } from "../Dashboard/insights";

export type CategoryResult = {
  answers: number;
  dontKnows: number;
  score: number;
};

const startOf = (survey: Pick<Survey, "startDate">) => String(survey.startDate).slice(0, 10);

// Finished surveys of the same type for the same user that ended before this one started. Newest first.
export const earlierSurveys = (all: Survey[], survey: Survey) =>
  all
    .filter(
      (s) =>
        s.id !== survey.id &&
        s.userId === survey.userId &&
        s.surveyType === survey.surveyType &&
        s.status === "finished" &&
        s.endDate < startOf(survey),
    )
    .sort((a, b) => b.endDate.localeCompare(a.endDate));

export const previousSurvey = (all: Survey[], survey: Survey) => earlierSurveys(all, survey)[0] || null;

// The average per question, where 0 is a don't know and is left out of the score.
export const categoryResults = (responses: Response[]) => {
  const results: Record<string, CategoryResult> = {};
  let total = 0;
  let answers = 0;
  let dontKnows = 0;
  responses.forEach((response) => {
    response.answers.forEach(({ question, value }) => {
      const result = results[question] || (results[question] = { answers: 0, dontKnows: 0, score: 0 });
      if (value > 0) {
        result.score += value;
        result.answers++;
        total += value;
        answers++;
      } else {
        result.dontKnows++;
        dontKnows++;
      }
    });
  });
  Object.values(results).forEach((result) => {
    result.score = result.answers === 0 ? 0 : result.score / result.answers;
  });
  return { questions: results, answers, dontKnows, totalScore: answers === 0 ? 0 : total / answers };
};

export const toCategoryScores = (results: Record<string, CategoryResult>): CategoryScores =>
  Object.fromEntries(Object.entries(results).filter(([, r]) => r.answers > 0).map(([question, r]) => [question, r.score]));

// How many surveys in a row, counted back from this one, had the same lowest category.
export const lowestStreakFor = (current: CategoryScores, earlier: Survey[], scoresById: Record<string, CategoryScores>) => {
  const category = lowestCategory(current);
  if (!category) {
    return null;
  }
  let count = 1;
  for (const survey of earlier) {
    if (lowestCategory(scoresById[survey.id]) !== category) {
      break;
    }
    count++;
  }
  return count >= 2 ? { category, count } : null;
};
